import omit from 'lodash/omit'
import sortBy from 'lodash/sortBy'
import uniqBy from 'lodash/uniqBy'

import type { ProductContent } from '@src/services/product.service'
import { QUERY_PARAMS } from '@src/utils/constants.util'
import { PRODUCT_TYPE_META } from '@src/utils/productMeta.util'

export function getVariantBySku<T extends ViewModel>(args: {
  viewModel: T
  sku: string | null
}): T['masterVariant'] | null {
  return args.viewModel.variants.find((variant) => variant.sku === args.sku) ?? null
}

/** Selects a variant for a product */
export function selectVariant<VM extends ViewModel>(args: {
  viewModel: VM
  variantSku?: string
}): VM {
  let { viewModel } = args

  const selectedVariant = args.viewModel.variants.find((variant) => variant.sku === args.variantSku)
  if (!selectedVariant) return viewModel

  viewModel = { ...viewModel, selectedVariant }
  viewModel = updateFacetOptions({ viewModel })
  return viewModel
}

/** Updates facet options based on the selected variant */
export function updateFacetOptions<VM extends ViewModel>(args: { viewModel: VM }): VM {
  const viewModel = { ...args.viewModel } as any

  // Update selected and available facets options based on the selected variant
  for (const facetKey in viewModel.facetOptions) {
    const facet = viewModel.facetOptions[facetKey]
    for (const option of facet) {
      /** An option is selected if it maches the facet of the currently selected variant. */
      option.isSelected = option.slug === viewModel.selectedVariant.facets[facetKey].slug

      /**
       * An option is available if there is at least one variant that matches
       * the facets of the currently selected variant and the option in loop.
       */
      option.isAvailable =
        getVariantsByFacets({
          viewModel: args.viewModel,
          facets: { ...viewModel.selectedVariant.facets, [facetKey]: option },
        }).length > 0
    }
  }

  return viewModel
}

/** Selects a variant for a product based on a facet option */
export function setFacetOption<VM extends ViewModel>(args: {
  viewModel: VM
  productTypeMeta: ProductTypeMeta
  facetKey: string
  facetOption: FacetOption
}) {
  const { viewModel, facetKey, facetOption } = args
  const facets = args.facetOption.isAvailable
    ? { ...args.viewModel.selectedVariant.facets, [facetKey]: facetOption }
    : { [facetKey]: facetOption }

  const [variant] = getVariantsByFacets({ viewModel, facets })

  const newViewModel = selectVariant({ viewModel, variantSku: variant?.sku })
  return newViewModel
}

export function getVariantFinalPrice(args: { variant: Commercetools.ProductVariant }): number {
  return (
    args.variant.prices?.[0]?.discounted?.value?.centAmount ??
    args.variant.prices?.[0]?.value?.centAmount ??
    0
  )
}

export function getVariantStrikedPrice(args: {
  variant?: Variant | null
  relativeToFacet: keyof NutritionFacets
  viewModel: ViewModel
}) {
  const { variant, relativeToFacet, viewModel } = args

  if (!variant) return null

  const variants = getVariantsByFacets({
    viewModel,
    facets: omit(viewModel.selectedVariant.facets, relativeToFacet),
  })

  const mostExpensiveVariant = variants.reduce((acc, variant) => {
    return variant.finalPrice > acc.finalPrice ? variant : acc
  }, variant)

  if (variant.originalPrice > mostExpensiveVariant.finalPrice) return variant.originalPrice
  if (mostExpensiveVariant.finalPrice > variant.finalPrice) return mostExpensiveVariant.finalPrice
  return null
}

/** Gets a facet option by slug */
export function getFacetOptionBySlug(args: {
  facetOptions: DigitalFacetOptions | NutritionFacetOptions | FitnessFacetOptions
  facetKey: string
  facetSlug: string
}): FacetOption | null {
  return (
    (args.facetOptions as any)[args.facetKey].find(
      (option: any) => option.slug === args.facetSlug
    ) ?? null
  )
}

/** Gets facet options by URL query params */
export function getFacetOptionsFromQueryParams(args: {
  facetOptions: DigitalFacetOptions | NutritionFacetOptions | FitnessFacetOptions
  queryParams: URLSearchParams
}) {
  const facetOptions = {} as any

  for (const facetKey of args.queryParams.keys()) {
    const isFacetKey = Object.keys(args.facetOptions).includes(facetKey)

    if (isFacetKey) {
      facetOptions[facetKey] = getFacetOptionBySlug({
        facetOptions: args.facetOptions,
        facetKey,
        facetSlug: args.queryParams.get(facetKey) ?? '',
      })
    }
  }
  return facetOptions as Partial<NutritionFacets>
}

export function isVariantActive(variant: Variant) {
  const now = new Date()
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const endDate = new Date(getAttribute(variant, 'endDate') || now)
  return endDate >= now
}

// Returns all variants that match the given facets
export function getVariantsByFacets<VM extends ViewModel>(args: {
  viewModel: VM
  facets: Partial<DigitalFacets | NutritionFacets | FitnessFacets>
}) {
  const variants = args.viewModel.variants.filter((variant) => {
    return Object.entries(args.facets).every(([facetKey, facetValue]) => {
      return (variant as any).facets[facetKey]?.slug === facetValue?.slug
    })
  })

  return sortVariantsByFacetPriority({
    variants,
    viewModel: args.viewModel,
    productTypeMeta: PRODUCT_TYPE_META,
  })
}

/**
 * Sort variants by the priority of the facets for the current product type.
 * Uses an array of sort functions ordered by facet priority.
 */
export function sortVariantsByFacetPriority<VM extends ViewModel>(args: {
  variants: Variant[]
  viewModel: VM
  productTypeMeta: ProductTypeMeta
}) {
  const { viewModel, variants, productTypeMeta } = args

  /**
   * @example: [
   *   (variant) => variant.facets.flavor.priority,
   *   (variant) => variant.facets.keyIngredient.priority
   * ]
   */
  const sortFunctions = productTypeMeta[viewModel.product.productType].facets
    .sort((facet) => facet.priority)
    .map((facet) => facet.key)
    .map((facetKey) => {
      return (variant: any) => variant.facets[facetKey].priority
    })

  return sortBy(variants, sortFunctions)
}

/** Sorts and dedupes facets based on a priority key */
export function sortAndDedupeFacets(args: { variants: any[]; facetKey: string }) {
  const { variants, facetKey } = args
  return variants
    .filter((a: any) => !!a.facets[facetKey].slug)
    .sort((a: any, b: any) => a.facets[facetKey].priority - b.facets[facetKey].priority)
    .reduce((acc, variant: any) => {
      const facet = variant.facets[facetKey]
      if (acc.some((option: any) => option.slug === facet.slug)) return acc
      acc.push({ ...facet, isAvailable: true, isSelected: false })
      return acc
    }, [])
}

/** Sorts and dedupes nutritional guarantees based on priority */
// TODO refactor this with the cart util parseGuarantees
export function parseGuarantees(guarantees: Commercetools.Guarantee[], localeCode?: string) {
  if (!guarantees) return []

  const guaranteesMap = new Map<string, Commercetools.Guarantee>()

  guarantees?.forEach((guarantee: any) => {
    guaranteesMap.set(guarantee?.obj?.key ?? '', {
      id: guarantee?.obj?.id ?? '',
      key: guarantee?.obj?.key ?? '',
      priority: guarantee?.obj?.value?.priority ?? 0,
      content: localeCode ? (guarantee?.obj?.value?.content?.[localeCode] ?? '') : '',
      icon: localeCode ? (guarantee?.obj?.value?.icon?.[localeCode] ?? '') : '',
    })
  })

  const guaranteesSorted = Array.from(guaranteesMap.values()).sort(
    (a, b) => a.priority - b.priority
  )
  return guaranteesSorted
}

/** Sorts and dedupes nutrionional disclaimers based on priority */
// TODO refactor this with the cart util parseDisclaimers
export function parseDisclaimers(disclaimers: Commercetools.Disclaimer[], localeCode?: string) {
  if (!disclaimers) return []

  const disclaimersMap = new Map<string, Commercetools.Disclaimer>()

  disclaimers?.forEach((disclaimer: any) => {
    disclaimersMap.set(disclaimer?.key ?? '', {
      id: disclaimer?.obj?.id ?? '',
      key: disclaimer?.obj?.key ?? '',
      priority: disclaimer?.obj?.value?.priority ?? 0,
      content: localeCode ? (disclaimer?.obj?.value?.content?.[localeCode] ?? '') : '',
    })
  })

  const disclaimersSorted = Array.from(disclaimersMap.values()).sort(
    (a, b) => a.priority - b.priority
  )
  return disclaimersSorted
}

/** Gets an attribute value from a variant */
export function getAttribute(
  variant: Commercetools.ProductVariant,
  attributeName: string,
  localeCode?: string
) {
  const attribute = variant.attributes?.find((attr) => attr.name === attributeName)
  if (localeCode) return attribute?.value?.[localeCode] ?? ''
  return attribute?.value ?? ''
}

/** Finds all variant assets that includes all the given tags */
export function getAssetsByTags(variant: Commercetools.ProductVariant, tags: string[]) {
  return variant.assets?.filter((asset) => tags.every((tag) => asset.tags?.includes(tag))) ?? []
}

/** Finds a variant asset that includes all the given tags */
export function getAssetByTags(variant: Commercetools.ProductVariant, tags: string[]) {
  return variant.assets?.find((asset) => tags.every((tag) => asset.tags?.includes(tag))) ?? null
}

/** Remove assets that don't contain a tag with the locale specified */
export function filterAssetsByLocale(
  productData: Commercetools.ProductData,
  locale: string = 'en-US'
) {
  const { masterVariant, variants }: any = productData
  masterVariant.assets = masterVariant.assets?.filter(hasLocale) ?? []
  variants.forEach((v: any) => {
    v.assets = v.assets?.filter(hasLocale) ?? []
  })

  function hasLocale(asset: Commercetools.Asset) {
    return asset.tags?.includes(`locale:${locale}`)
  }
}

export function sortAssetsByPriority(assets: Commercetools.Asset[]) {
  return sortBy(assets, (asset) => {
    const priority = asset.tags?.find((tag) => tag.startsWith('priority:'))
    return priority
  })
}

/** Get product assets from variants where asset has tag 'type:product' */
export function getProductAssets(productData: Commercetools.ProductData) {
  let productAssets = [productData.masterVariant, ...productData.variants].flatMap((v) =>
    getAssetsByTags(v, ['type:product'])
  )

  productAssets = uniqBy(productAssets, (a) => a.sources?.[0]?.uri)

  return productAssets
}

/** Updates the URL query params based on the view model */
export function updateURLQueryParams<VM extends ViewModel>(args: { viewModel: VM }) {
  const { viewModel } = args
  const url = new URL(location.href)
  url.searchParams.set(QUERY_PARAMS.sku, viewModel.selectedVariant.sku ?? '')
  url.searchParams.set(QUERY_PARAMS.quantity, viewModel.quantity.toString())
  window.history.replaceState({}, '', url)
}

export function getValidQuantity(quantity: number, productType: keyof ProductTypeMeta) {
  const maxQuantity = PRODUCT_TYPE_META[productType].maxQuantity
  if (quantity < 1) return 1
  if (quantity > maxQuantity) return maxQuantity
  return quantity
}

export function getMaxQuantity(productType: keyof ProductTypeMeta) {
  return PRODUCT_TYPE_META[productType].maxQuantity
}

/** Gets an image URL and alt text from an asset */
export function getImage(asset: Commercetools.Asset, locale: string = 'en-US') {
  const url = asset.sources?.[0]?.uri ?? ''
  const alt = asset.custom?.fields?.altText?.[locale] ?? ''
  return { url, alt }
}

/** Gets the localized product key for a given locale */
export function getProductKeyForLocale(
  productKey: string,
  productContent: ProductContent | null,
  localeCode: string
): string | undefined {
  const productKeyForLocales = productContent?.metadata?.productKey
  if (!productKeyForLocales || !Object.values(productKeyForLocales).includes(productKey)) {
    return undefined
  }
  if (productKeyForLocales) {
    const productKeyForLocale = (productKeyForLocales as Record<string, string>)[localeCode]
    return productKeyForLocale ?? productKeyForLocales.Default
  }
}
